import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { parsedRevisionNumberWithSubLetter } from 'common/revisionNumber'
import { useWindowSize } from 'react-use'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

const CustomStack = ({ sx, children }) => (
  <Stack
    minHeight="0"
    minWidth="0"
    overflow="hidden"
    gridRow="span 2"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '0',
      height: '50%',
      gap: '20px',
      overflow: 'hidden',
      ...sx
    }}>
    {children}
  </Stack>
)

export const DeckLayoutContainer = ({
  Deck,
  headerTitle,
  buttons,
  topRightElement,
  bottomRightElement,
  topRightSx,
  bottomRightSx,
  deckRevision,
  canToggle = false,
  showLayoutToggle = false,
  disableToggles = false
}) => {
  const {
    screen,
    deck,
    deckRevision: deckRevisionContext,
    currentMaintenanceView,
    setCurrentMaintenanceView
  } = useCurrentDeckContext()
  const deckRevision_ = deckRevision ?? deckRevisionContext
  const [heightConfigs, setHeightConfig] = useState({
    header: 80,
    container: undefined
  })

  const window = useWindowSize()

  useEffect(() => {
    setCurrentMaintenanceView(showLayoutToggle ? 'layout' : undefined)
  }, [])

  const headerRef = useRef()
  const containerRef = useRef()
  const theme = useTheme()
  const deckInfo = `${deck.DeckHeader.DeckLevel} ${deck.DeckHeader.MaterialNumber}`
  const title = screen ? `${screen.Name} - ${deckInfo}` : deckInfo

  useLayoutEffect(() => {
    const timer = setInterval(() => {
      if (!containerRef.current || !headerRef.current) {
        return
      }

      setHeightConfig({
        header: headerRef.current?.clientHeight || 80,
        container: containerRef.current?.clientHeight
      })
      clearInterval(timer)
    }, 100)

    return () => {
      clearInterval(timer)
    }
  }, [window])

  const getToggleButtons = () => {
    if (canToggle) {
      return (
        <ToggleButtonGroup
          exclusive
          disabled={disableToggles}
          size="small"
          sx={{
            border: 'none'
          }}
          onChange={(_, newValue) => {
            if (newValue) {
              setCurrentMaintenanceView(newValue)
            }
          }}
          value={currentMaintenanceView}>
          {showLayoutToggle && (
            <ToggleButton
              value="layout"
              sx={{
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderTopLeftRadius: '20px !important',
                borderBottomLeftRadius: '20px !important',
                textTransform: 'lowercase'
              }}>
              layout
            </ToggleButton>
          )}
          <ToggleButton
            value="mm"
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.12)',
              ...(!showLayoutToggle && {
                borderTopLeftRadius: '20px !important',
                borderBottomLeftRadius: '20px !important'
              }),
              textTransform: 'lowercase'
            }}>
            mm
          </ToggleButton>
          <ToggleButton
            value="%"
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.12)',
              textTransform: 'lowercase'
            }}>
            %
          </ToggleButton>
          <ToggleButton
            value="action"
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderTopRightRadius: '20px !important',
              borderBottomRightRadius: '20px !important',
              textTransform: 'lowercase'
            }}>
            Action
          </ToggleButton>
        </ToggleButtonGroup>
      )
    }
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `calc(100% - ${heightConfigs.header}px)`
      }}>
      <Grid
        container
        boxShadow="0px 10px 15px -3px rgba(0,0,0,0.1);"
        zIndex={1200}
        display="flex"
        bgcolor={theme.palette.supporting.dark}
        alignItems="center"
        justifyContent="space-between"
        ref={headerRef}
        mb={1}
        flexDirection="row">
        <Grid item>
          <Typography variant="h1"> {headerTitle}</Typography>
        </Grid>
        <Grid item container gap={1} width="max-content">
          {buttons}
        </Grid>
      </Grid>
      <Box
        display="flex"
        sx={{
          height: heightConfigs.container - heightConfigs.header
        }}
        flexDirection="row"
        justifyContent="space-between"
        width="100%"
        columnGap="10px">
        {/* Left Side */}
        <Box
          display="flex"
          width="50%"
          padding="1em"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 20px)',
            backgroundColor: 'transparent',
            borderRadius: 2,
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05));'
          }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className="title" variant="h5" component="h5">
              {title}
            </Typography>
            {getToggleButtons()}
          </Box>

          {deckRevision_ && (
            <Typography className="deck-canvas-info" component="p" sx={{ userSelect: 'none', mb: 2 }}>
              {`Revision: ${parsedRevisionNumberWithSubLetter(deckRevision_)} ${
                deckRevision_.IsTrial ? '(TRIAL)' : ''
              } ${!deckRevision_.IsPublished ? '(UNPUBLISHED)' : ''}`}
            </Typography>
          )}

          {Deck}
        </Box>

        {/* Right Side (Top & Bottom Sections) */}
        <Box
          display="flex"
          width="50%"
          flexDirection="column"
          sx={{
            height: !bottomRightElement ? 'calc(100% - 20px)' : heightConfigs.container - heightConfigs.header - 6,
            overflowY: !bottomRightElement ? 'scroll' : undefined
          }}>
          {bottomRightElement ? (
            <>
              <CustomStack sx={{ ...topRightSx }}>{topRightElement}</CustomStack>
              <CustomStack sx={{ marginTop: '1em', ...bottomRightSx }}>{bottomRightElement}</CustomStack>
            </>
          ) : (
            topRightElement
          )}
        </Box>
      </Box>
    </Box>
  )
}
